import { appendReviewId, reviewRoutePaths } from '../../../../utils/reviewRoutePaths';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDetailReturn, useDetailViewState } from '../detail/DetailReturnState';
import BrowseLink from '../../../../features/browse/BrowseLink';
import { useWebLocale } from '../../../../contexts/WebLocaleContext';
import { useColorScheme } from '../../../../contexts/ColorSchemeContext';
import ProfileIdentity from '../../../../components/content/ProfileIdentity';
import baseline, { appTextStyle } from '../../../../app-ui/baseline';
import { albumMemberReviews } from './albumMemberReviews';
import './UserReviewSection.css';

function Chevron() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <path d="m9 5 7 7-7 7" />
    </svg>
  );
}
export function ReviewRow({
  review,
  basePath,
  en,
  onReviewOpen,
  onAuthorOpen,
  full = false,
  own = false,
}) {
  const NavigationLink = useDetailReturn() ? Link : BrowseLink;
  const user = review.user || {};
  const name = user.nickname || '';
  const [failedImage, setFailedImage] = useState(null);
  const displayDate = own ? review.updatedAt || review.createdAt : review.createdAt;
  const score = Number(review.rating || 0).toFixed(1);
  const reviewPath = appendReviewId(basePath, review.id);
  const bodyRef = useRef(null);
  const [overflow, setOverflow] = useState(false);
  useLayoutEffect(() => {
    if (!full) return undefined;
    const measure = () =>
      setOverflow(
        Boolean(bodyRef.current && bodyRef.current.scrollHeight > bodyRef.current.clientHeight + 1),
      );
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (bodyRef.current) observer?.observe(bodyRef.current);
    return () => observer?.disconnect();
  }, [full, review.content]);
  return (
    <article className="pp-album-member-row" data-review-id={review.id}>
      <div className="pp-album-member-identity">
        <NavigationLink
          className="pp-album-member-author"
          onClick={() => onAuthorOpen?.(review)}
          to={`/users/${encodeURIComponent(name)}`}
          aria-label={en ? `Open ${name}'s profile` : `${name} 프로필 보기`}
        >
          <span className="pp-album-member-avatar" aria-hidden="true">
            {name.slice(0, 1)}
            {user.profileImageUrl && failedImage !== user.profileImageUrl && (
              <img
                src={user.profileImageUrl}
                alt=""
                onError={() => setFailedImage(user.profileImageUrl)}
              />
            )}
          </span>
          <ProfileIdentity name={name} mark={user.profileMark} avatar={false} />
        </NavigationLink>
        <span
          className="pp-album-member-score"
          style={appTextStyle(baseline.contentText.score)}
          aria-label={`${en ? 'Rating' : '평점'} ${score}`}
        >
          <svg
            viewBox="0 0 24 24"
            width={baseline.contentRating.starSize}
            height={baseline.contentRating.starSize}
            aria-hidden="true"
          >
            <path
              fill="#F16438"
              transform="translate(0 .955)"
              d="M12 2L14.47 8.6L21.51 8.91L15.99 13.3L17.88 20.09L12 16.2L6.12 20.09L8.01 13.3L2.49 8.91L9.53 8.6L12 2Z"
            />
          </svg>
          {score}
        </span>
      </div>
      <NavigationLink
        className="pp-album-member-detail"
        onClick={() => onReviewOpen?.(review)}
        to={reviewPath}
        aria-label={en ? `${name}'s review` : `${name} 리뷰 상세`}
      >
        <p
          ref={bodyRef}
          className="pp-album-member-body"
          style={{
            ...appTextStyle(baseline.contentText.body),
            ...(full && { WebkitLineClamp: 10 }),
          }}
        >
          {review.content.trim()}
        </p>
        {full && overflow && (
          <span className="pp-member-full-label">{en ? 'View Full Review' : '전체보기'}</span>
        )}
        <p className="pp-album-member-meta" style={appTextStyle(baseline.contentText.metadata)}>
          {en ? 'Likes' : '좋아요'} {review.likeCount ?? 0} · {en ? 'Comments' : '댓글'}{' '}
          {review.comments?.length ?? 0}
          {full && !own ? ` · ${en ? 'Saved' : '저장'} ${review.bookmarkCount ?? 0}` : ''}
          {displayDate ? ` · ${displayDate.slice(0, 10).replaceAll('-', '.')}` : ''}
        </p>
      </NavigationLink>
    </article>
  );
}

// Only album detail uses this preview; full review lists/details retain their own contracts.
export default function UserReviewSection({
  reviews = [],
  myReviewId,
  loading = false,
  error = false,
  onRetry,
  onReviewOpen,
  onAuthorOpen,
}) {
  const NavigationLink = useDetailReturn() ? Link : BrowseLink;
  const { locale } = useWebLocale();
  const { resolvedMode } = useColorScheme();
  const en = locale === 'en';
  const colors = baseline.tokens[resolvedMode === 'dark' ? 'darkColors' : 'lightColors'];
  const { pathname, search } = useLocation();
  const basePath = reviewRoutePaths(pathname, search).list;
  const { count, publicReviews } = albumMemberReviews(reviews, myReviewId);
  const [expanded, setExpanded] = useDetailViewState(`member-preview:${basePath}`, false);
  const [showFullView, setShowFullView] = useState(false);
  const listRef = useRef(null);
  const canExpand = publicReviews.length > 5 && !expanded;
  const shown = publicReviews.slice(0, expanded ? 10 : 5);
  const expandCount = Math.min(5, Math.max(0, publicReviews.length - 5));
  useLayoutEffect(() => {
    const measure = () =>
      setShowFullView(
        (listRef.current?.getBoundingClientRect().height || 0) + 42 > window.innerHeight * 0.42,
      );
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (listRef.current) observer?.observe(listRef.current);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [publicReviews.length, expanded, locale, loading, error]);
  const actionLabel = en ? `View all ${count} reviews` : `리뷰 전체 보기, ${count}개`;
  return (
    <section
      className="pp-album-member-section"
      aria-label={en ? 'Member Reviews & Ratings' : '회원 리뷰·평가'}
      style={{
        '--member-background': colors.background,
        '--member-text': colors.text,
        '--member-secondary': colors.textSecondary,
        '--member-body': colors.reviewBody,
        '--member-divider': colors.divider,
        '--member-border': colors.dividerStrong,
        '--member-muted': colors.surfaceMuted,
      }}
    >
      <h2 style={appTextStyle(baseline.tokens.typography.sectionTitle)}>
        <NavigationLink to={basePath}>
          {en ? 'Member Reviews & Ratings' : '회원 리뷰·평가'}
          <Chevron />
        </NavigationLink>
      </h2>
      {loading ? (
        <p role="status">{en ? 'Loading…' : '불러오는 중…'}</p>
      ) : error ? (
        <div role="alert">
          <p>{en ? 'Could not load reviews.' : '리뷰를 불러오지 못했어요.'}</p>
          <button type="button" onClick={onRetry}>
            {en ? 'Retry' : '다시 시도'}
          </button>
        </div>
      ) : publicReviews.length ? (
        <>
          <div ref={listRef} className={`pp-album-member-list${canExpand ? ' can-expand' : ''}`}>
            {shown.map((review) => (
              <ReviewRow
                key={review.id}
                review={review}
                basePath={basePath}
                en={en}
                onReviewOpen={onReviewOpen}
                onAuthorOpen={onAuthorOpen}
              />
            ))}
            {canExpand && <div className="pp-album-member-fade" aria-hidden="true" />}
          </div>
          {(canExpand || (expanded && publicReviews.length > 5) || showFullView) &&
            (canExpand ? (
              <button
                className="pp-album-member-more is-preview"
                type="button"
                aria-label={
                  en
                    ? `View ${expandCount} more member reviews`
                    : `회원 리뷰 ${expandCount}개 더 보기`
                }
                onClick={() => setExpanded(true)}
              >
                {en ? 'More' : '더보기'}
              </button>
            ) : (
              <NavigationLink
                className="pp-album-member-more"
                to={basePath}
                aria-label={actionLabel}
              >
                {en ? `View All · ${count}` : `전체보기 · ${count}개`}
              </NavigationLink>
            ))}
        </>
      ) : (
        <div className="pp-album-member-empty">
          <p style={appTextStyle(baseline.tokens.typography.bodyMedium)}>
            {myReviewId
              ? en
                ? 'No other member reviews yet'
                : '아직 다른 회원 리뷰가 없습니다'
              : en
                ? 'No member reviews yet'
                : '아직 회원 리뷰가 없습니다'}
          </p>
          <p style={appTextStyle(baseline.contentText.metadata)}>
            {en ? 'New reviews will appear here.' : '새로운 리뷰가 등록되면 이곳에서 볼 수 있어요.'}
          </p>
        </div>
      )}
    </section>
  );
}
